import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {
  BoardGuests,
  DataObjectId,
  GenResp,
  GuestPublicInfo,
  isDataObjectId,
  SharedBoardRecords,
  SharedBoardsCredResp,
  SharedBoardSettings,
  SharingGuest
} from 'yougile-model'
import {Storage} from '@client/storage'
import {Tracking} from '@common/tracking'
import {TrackingEvent} from '@common/tracking-events'
import {Backend} from '../../common/backend'
import {MessageBox} from '../components/message-box'
import {Sync} from './sync'

const guestIdStorageName = 'guest-id'
const guestKeyStorageName = 'guest-key'

export class SharedBoardsClient {
  static sharedId = Storage.getSharedId()

  static companyId: DataObjectId
  static boardId: DataObjectId
  static guestId: string
  static guestKey: string
  static boardGuests: BoardGuests
  static settings: SharedBoardSettings
  static guest: SharingGuest

  static getId(): string | undefined {
    return SharedBoardsClient.sharedId
  }

  static isGuest(): boolean {
    return !!this.getId()
  }

  static requestCred(): Promise<SharedBoardsCredResp & GenResp> {
    const userId = Storage.getWithoutPrefix('user_id')
    const userKey = Storage.getWithoutPrefix('user_key')
    return Backend.postJSON<SharedBoardsCredResp & GenResp>(
      '/data/shared-boards/cred',
      {
        id: this.sharedId,
        guestId: this.guestId,
        guestKey: this.guestKey,
        userId,
        userKey
      },
      {rawErrors: true}
    )
  }

  private static readStoredCreadentials(): void {
    this.guestId = Storage.getItem(guestIdStorageName)
    this.guestKey = Storage.getItem(guestKeyStorageName)
  }

  private static storeCredentials(): void {
    Storage.setItem(guestIdStorageName, this.guestId)
    Storage.setItem(guestKeyStorageName, this.guestKey)
  }

  /**
   * Loads special anonymous userId and key for shared-board access and stores them
   * in Backend.
   */
  static async loadCredentials(): Promise<boolean> {
    this.readStoredCreadentials()
    const res = await this.requestCred()
    if (res.result !== 'ok') {
      return false
    }
    this.boardId = res.boardId
    this.settings = {
      background: res.background,
      lang: res.lang,
      theme: res.theme,
      type: res.shareType
    }
    this.guestId = res.guestId
    this.guestKey = res.guestKey
    this.guest = res.guest
    this.boardGuests = res.guests
    this.storeCredentials()

    if (!isDataObjectId(res.guestId)) {
      Tracking.send(TrackingEvent.shareGuestVisit)
    } else {
      Tracking.send(TrackingEvent.shareUserVisit)
    }
    this.companyId = res.companyId
    Backend.setCredentials(res.userId, res.key)
    return true
  }

  static async listSharedBoards(
    companyId: DataObjectId
  ): Promise<SharedBoardRecords> {
    if (this.getId()) {
      return {}
    }
    const res = await Backend.postJSON<{boards: SharedBoardRecords} & GenResp>(
      '/data/shared-boards/list',
      {companyId, ...Backend.loadCred()}
    )
    if (res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
      return {}
    }
    return res.boards
  }

  static async changeGuest(props: {
    name?: string
    avatar?: string
    readChatId?: DataObjectId
    sharedIdToRemove?: string
  }): Promise<void> {
    const isUser = !this.guestId
    const guestId = isUser ? Backend.loadId() : this.guestId
    const guestKey = isUser ? Backend.loadKey() : this.guestKey
    const box = Sync.getCurrentSandbox()

    const res = await Backend.postJSON<GenResp>(
      '/data/shared-boards/guest/change',
      {guestId, guestKey, readCompanyId: box.companyId, ...props},
      {rawErrors: true}
    ).catch((e: unknown) => {
      logger.error(e)
      return
    })
    if (!res || res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
    }
  }

  static async changeBoardLink(
    companyId: DataObjectId,
    sharedId: string,
    opts: SharedBoardSettings
  ): Promise<void> {
    const res = await Backend.postJSON<GenResp>('/data/shared-boards/change', {
      companyId,
      sharedId,
      ...opts,
      ...Backend.loadCred()
    })
    if (res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
    }
  }

  static async addBoardLink(
    companyId: DataObjectId,
    boardId: DataObjectId
  ): Promise<string | undefined> {
    const res = await Backend.postJSON<{sharedId: string} & GenResp>(
      '/data/shared-boards/add',
      {
        companyId,
        boardId,
        lang: Lang.getLang(),
        ...Backend.loadCred()
      }
    )
    if (res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
      return undefined
    }
    return res.sharedId
  }

  static async deleteBoardLink(sharedId: string): Promise<void> {
    const res = await Backend.postJSON<GenResp>('/data/shared-boards/delete', {
      sharedId,
      ...Backend.loadCred()
    })
    if (res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
    }
  }

  static getGuestInfo(guestId: string): GuestPublicInfo | undefined {
    return this.boardGuests?.[guestId]
  }

  static async loadBoardList(): Promise<SharedBoardRecords> {
    const res = await Backend.postJSON<{boards: SharedBoardRecords} & GenResp>(
      '/data/shared-boards/list-boards',
      {...Backend.loadCred()}
    )
    if (res.result !== 'ok') {
      MessageBox.show(Lang.msg.common.serverFailTryLater, 'danger')
      return {}
    }
    return res.boards
  }
}
